// app/ask-cimigo/components/PlanCard.tsx
import Button, { BtnType } from "@/components/common/buttons/Button";
import DoneIcon from "@/components/icons/DoneIcon";
import { useUserQuery } from "@/hooks/Query/useUserQuery";
import { cn } from "@/lib/utils";
import { Content, subscription } from "@/models/subscription";
import { APP_ROUTES } from "@constants/routes";
import CloseIcon from "@mui/icons-material/Close";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React from "react";

interface PlanFeatureProps {
  name: string;
  isComplete: boolean;
}
interface PlanDescriptionProps {
  saving: number;
}

const PlanHeader = ({ children }: { children: React.ReactNode }) => (
  <h2 className="m-0 text-xl/8 font-bold text-cimigo-gray-1100 min-h-16">
    {children}
  </h2>
);
const PlanPrice = ({ children }: { children: React.ReactNode }) => {
  const t = useTranslations("messages");
  return (
    <p className="m-0 text-[32px]/[48px] font-semibold text-cimigo-gray-1100">
      {children}{" "}
      <span
        className="text-[13px]/[48px] text-gray-50 font-semibold"
        translation-key="common_moth"
      >
        {t("common_moth")}
      </span>
    </p>
  );
};
const PlanDescription = ({ saving }: PlanDescriptionProps) => {
  const t = useTranslations("messages");
  return (
    <Box className={"h-[36px]"}>
      <p className="m-0 text-cimigo-gray-900 text-xs/3 font-normal tracking-[0.18px]">
        <span translation-key="common_billed_annually">
          {t("common_billed_annually")}
        </span>{" "}
        {saving > 0 ? (
          <span
            className="m-0 font-semibold text-[#17497F] text-xs/3  tracking-[0.18px]"
            translation-key="common_saving"
          >
            {t("common_saving", { saving })}
          </span>
        ) : null}{" "}
        <span translation-key="common_tax_exclusive">
          {t("common_tax_exclusive")}
        </span>
      </p>
    </Box>
  );
};
const PlanBestValue = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0 mt-6 text-cimigo-blue text-sm/6 font-semibold ">
    {children}
  </p>
);
const PlanDetails = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0  text-gray-90 text-sm/6 font-normal">{children}</p>
);
const PlanButton = ({
  subscriptionId,
  trial,
  disable,
}: {
  subscriptionId: number;
  trial: boolean;
  disable?: boolean;
}) => {
  const { data: user } = useUserQuery({
    options: {
      enabled: false,
    },
  });
  const t = useTranslations("messages");
  const router = useRouter();
  const locale = useLocale();
  const handleGotoPayment = () => {
    if (!user) {
      router.push(
        `${APP_ROUTES(locale).REGISTER.to}?type=plan&redirect=${encodeURIComponent(`/payment/${subscriptionId}/preview?module=plan`)}`,
      );
      return;
    }

    router.push(
      APP_ROUTES(locale).PAYMENT.SUBSCRIPTION.ROOT.to.replace(
        ":id",
        `${subscriptionId}`,
      ),
    );
  };

  return (
    <React.Fragment>
      <Button
        btnType={BtnType.Raised}
        sx={{ mt: "24px", mb: "32px" }}
        onClick={handleGotoPayment}
        className="plan-card-subscribe-button"
      >
        <span
          className="text-lg/8 font-medium"
          translation-key="common_subscribe_now"
        >
          {t("common_subscribe_now")}
        </span>
      </Button>
    </React.Fragment>
  );
};

const PlanFeature = ({ name, isComplete }: PlanFeatureProps) => (
  <Box className="pt-4 flex flex-col gap-6 ">
    <Box className="flex flex-row gap-1">
      {isComplete ? (
        <DoneIcon fillColorPath="var(--cimigo-blue-light-1)" />
      ) : (
        <CloseIcon className="text-red-600" />
      )}
      <span className="text-sm/6 font-normal text-gray-90">{name}</span>
    </Box>
  </Box>
);

export default function PlanCard({
  title,
  price,
  valueNote,
  description,
  features,
  isPopular = false,
  subscriptionId,
  saving,
  trail,
  disable,
  prevSubscription,
  isOnlyOne,
}: {
  title: string;
  price: string;
  valueNote: string;
  description: string;
  features: Content[];
  isPopular?: boolean;
  subscriptionId: number;
  trail: boolean;
  saving: number;
  disable?: boolean;
  prevSubscription?: subscription;
  isOnlyOne?: boolean;
}) {
  const t = useTranslations("messages");
  return (
    <div
      className={cn(
        `px-6 pb-9 rounded-lg !min-h-[100%] ${
          isPopular ? "bg-ciBlue-1 pt-6" : "bg-ghost-white pt-[74px] "
        }`,
        {
          "grid grid-cols-1 md:grid-cols-[5fr_7fr] p-0 shadow-xl rounded-xl":
            isOnlyOne,
        },
      )}
    >
      <div
        className={cn("", {
          "px-8 py-6 md:px-8 md:py-6 xl:px-12 xl:py-10 bg-ghost-white  rounded-t-xl md:rounded-l-xl md:rounded-t-none":
            isOnlyOne,
        })}
      >
        {isPopular && (
          <div className="mb-4 px-4 py-1 rounded-[32px] bg-[#3B87DB] text-white inline-block">
            <span
              className="text-sm/6 font-medium"
              translation-key="common_most_popular"
            >
              {t("common_most_popular")}
            </span>
          </div>
        )}
        <PlanHeader>{title}</PlanHeader>
        <PlanPrice>{price}</PlanPrice>
        <PlanDescription saving={saving} />
        <PlanBestValue>{valueNote}</PlanBestValue>
        <PlanDetails>{description}</PlanDetails>
        <PlanButton
          subscriptionId={subscriptionId}
          trial={trail}
          disable={disable}
        />
      </div>
      <Box
        className={cn("", {
          " md:border-l md:border-t-0 px-8 py-8 xl:px-10 xl:py-10 bg-ciBlue-1 rounded-b-xl md:rounded-r-xl md:rounded-b-none": isOnlyOne,
        })}
      >
        <Box className="flex items-center gap-3">
          <span className="h-px flex-1 bg-[#E8E8E8]" />
          <Typography
            className="text-lg text-cimigo-blue font-semibold whitespace-nowrap"
            translation-key="common_what_you_get"
          >
            {t("common_what_you_get")}
          </Typography>
          <span className="h-px flex-1 bg-[#E8E8E8]" />
        </Box>
        {prevSubscription && (
          <Box className="flex justify-start items-start mb-2 mt-3">
            <Typography
              sx={{
                color: "var(--cimigo-blue)",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "24px",
              }}
            >
              {prevSubscription?.name + t("common_pricing_plus")}
            </Typography>
          </Box>
        )}
        <div
          className={cn("", {
            "grid grid-cols-1 md:grid-cols-2 md:gap-x-2": isOnlyOne,
          })}
        >
          {features
            .filter((item) => !item.isHidden)
            .map((feature: Content, index) => (
              <PlanFeature
                key={index}
                name={feature.name}
                isComplete={feature.isComplete}
              />
            ))}
        </div>
      </Box>
    </div>
  );
}
